/**
 * Profiles Service
 * Handles all profile-related data operations
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database, Tables, TablesUpdate } from '@/integrations/supabase/types';
import { logger } from '@/lib/logger';
import { handleError, getSupabaseErrorMessage } from '@/lib/errorHandler';

export type Profile = Tables<'profiles'>;

export interface UpdateProfileInput extends Partial<TablesUpdate<'profiles'>> {
  id: string;
}

export class ProfilesService {
  /**
   * Get user profile by ID
   */
  static async getProfile(userId: string): Promise<Profile | null> {
    logger.supabase('SELECT', 'profiles', { userId });
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      logger.error('[ProfilesService] Error fetching profile', error, { userId });
      throw new Error(getSupabaseErrorMessage(error));
    }

    return data as Profile | null;
  }

  /**
   * Update user profile
   */
  static async updateProfile(input: UpdateProfileInput): Promise<Profile> {
    const { id, ...updates } = input;

    const { data, error } = await supabase
      .from('profiles')
      .update(updates as any)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('[ProfilesService] Error updating profile:', error);
      throw new Error(`Failed to update profile: ${error.message}`);
    }

    return data as Profile;
  }
}

